import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Send, Lock, Vote, X, Search, CheckCircle2, Check, Clock, ListFilter, BarChart3, RotateCcw, Eye, EyeOff, LockKeyhole, Loader2, Link2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { API, norm } from '@/lib/helpers';
import VotacaoFormDialog, { emptyForm } from './VotacaoFormDialog';

const FILTROS = [
  { key: 'todos', label: 'Todas', icon: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { key: 'nao_enviada', label: 'Rascunho', icon: 'bg-slate-100', iconColor: 'text-slate-600' },
  { key: 'enviada', label: 'Enviadas', icon: 'bg-blue-100', iconColor: 'text-blue-600' },
  { key: 'encerrada', label: 'Encerradas', icon: 'bg-red-100', iconColor: 'text-red-600' },
];

const STATUS_MAP = {
  nao_enviada: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700' },
  enviada: { label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  respondida: { label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  encerrada: { label: 'Encerrada', color: 'bg-red-100 text-red-700' },
};

export default function VotacoesTab() {
  const [votacoes, setVotacoes] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [detailVotacao, setDetailVotacao] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchVotacoes = async () => {
    try {
      const res = await fetch(`${API}/votacoes`, { credentials: 'include' });
      if (res.ok) setVotacoes(await res.json());
    } catch {}
  };

  useEffect(() => { fetchVotacoes(); }, []);

  const counts = {
    todos: votacoes.length,
    nao_enviada: votacoes.filter(v => v.status === 'nao_enviada').length,
    enviada: votacoes.filter(v => v.status === 'enviada' || v.status === 'respondida').length,
    encerrada: votacoes.filter(v => v.status === 'encerrada').length,
  };

  const filtered = votacoes.filter(v => {
    if (filter === 'nao_enviada' && v.status !== 'nao_enviada') return false;
    if (filter === 'enviada' && v.status !== 'enviada' && v.status !== 'respondida') return false;
    if (filter === 'encerrada' && v.status !== 'encerrada') return false;
    if (search && !norm(v.pergunta).includes(norm(search))) return false;
    return true;
  });

  const handleSave = () => {
    setFormOpen(false); setEditingId(null); setForm({ ...emptyForm }); fetchVotacoes();
  };

  const handleAction = async (votacaoId, action) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/votacoes/${votacaoId}/${action}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        if (res.status === 401) throw new Error('Sessão expirada. Faça login novamente.');
        const text = await res.text();
        try { const e = JSON.parse(text); throw new Error(e.detail || 'Erro'); } catch { throw new Error(text || `Erro ${res.status}`); }
      }
      const msgs = { enviar: 'Votação enviada aos auxiliares', encerrar: 'Votação encerrada', reabrir: 'Votação reaberta como rascunho' };
      toast.success(msgs[action] || 'Ação realizada');
      fetchVotacoes(); setConfirmAction(null);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (votacaoId) => {
    try {
      const res = await fetch(`${API}/votacoes/${votacaoId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erro'); }
      toast.success('Votação excluída'); fetchVotacoes(); setConfirmAction(null);
    } catch (err) { toast.error(err.message); }
  };

  const openEdit = (v) => {
    setEditingId(v.votacao_id);
    setForm({
      pergunta: v.pergunta, tipo: v.tipo, descricao: v.descricao || '', mostrar_descricao: v.mostrar_descricao,
      opcoes: v.opcoes?.length ? v.opcoes.map(o => ({ texto: o.texto, correta: o.correta })) : [{ texto: '', correta: false }, { texto: '', correta: false }],
      permitir_multiplas: v.permitir_multiplas, marcar_correta: v.marcar_correta, anonimo: v.anonimo || false, resultado_visibilidade: v.resultado_visibilidade,
    });
    setFormOpen(true);
  };

  const openDetail = async (v) => {
    setDetailVotacao(v);
    setRespostas([]);
    try {
      const res = await fetch(`${API}/votacoes/${v.votacao_id}/respostas`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRespostas(data.respostas);
        setDetailVotacao(data.votacao || v);
      }
    } catch {}
  };

  // Polling a cada 5s quando modal de resultados está aberto
  const pollingRef = useRef(null);
  useEffect(() => {
    if (detailVotacao) {
      pollingRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API}/votacoes/${detailVotacao.votacao_id}/respostas`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setRespostas(data.respostas);
          }
        } catch {}
      }, 5000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [detailVotacao]);

  const openAdd = () => { setEditingId(null); setForm({ ...emptyForm }); setFormOpen(true); };

  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return dt.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) + ' ' + dt.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Vote className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Votações</h2>
                  <Badge className="bg-indigo-100 text-indigo-700 text-sm font-bold">{counts.todos}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="votacoes-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-votacao-${f.key}`}
                  className={`w-full lg:w-full min-w-[140px] lg:min-w-0 text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.icon}`}>
                      <ListFilter className={`w-4 h-4 ${f.iconColor}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-indigo-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={openAdd} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-criar-votacao">
                <Plus className="w-4 h-4 mr-1" />Nova Votação
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Vote className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'votação' : 'votações'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="votacoes-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="votacoes-list">
              {filtered.map(v => {
                const st = STATUS_MAP[v.status] || STATUS_MAP.nao_enviada;
                return (
                  <div key={v.votacao_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`votacao-${v.votacao_id}`}>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 font-medium">{v.pergunta}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />{fmtDate(v.created_at)}
                          </span>
                          <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                          <Badge variant="outline" className="text-[10px]">{v.tipo === 'texto' ? 'Texto' : 'Múltipla escolha'}</Badge>
                          {v.total_respostas > 0 && <span className="text-[10px] text-slate-400">{v.total_respostas} {v.total_respostas === 1 ? 'resposta' : 'respostas'}</span>}
                        </div>
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0 items-center flex-wrap justify-end">
                        {v.reuniao_id ? (<>
                          {(v.status === 'enviada' || v.status === 'respondida' || v.status === 'encerrada') && (
                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-600 hover:bg-blue-50 text-xs h-8" onClick={() => openDetail(v)} data-testid={`ver-respostas-${v.votacao_id}`}><BarChart3 className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Respostas</span></Button>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="h-8 w-8 flex items-center justify-center"><Link2 className="w-4 h-4 text-slate-400" /></div>
                              </TooltipTrigger>
                              <TooltipContent><p>Vinculada a uma reunião. Administre pela aba Reuniões.</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </>) : (<>
                          {v.status === 'nao_enviada' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-100" onClick={() => openEdit(v)} title="Editar" data-testid={`editar-votacao-${v.votacao_id}`}><Edit className="w-4 h-4" /></Button>
                          )}
                          {v.status === 'encerrada' && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50" onClick={() => setConfirmAction({ type: 'reabrir', votacao: v })} data-testid={`reabrir-votacao-${v.votacao_id}`}><RotateCcw className="w-4 h-4" /></Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Reabrir como rascunho (as respostas serão apagadas)</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setConfirmAction({ type: 'excluir', votacao: v })} title="Excluir" data-testid={`excluir-votacao-${v.votacao_id}`}><Trash2 className="w-4 h-4" /></Button>
                          {v.status === 'nao_enviada' && (
                            <Button variant="outline" size="sm" className="text-green-600 border-green-600 hover:bg-green-50 text-xs h-8" onClick={() => setConfirmAction({ type: 'enviar', votacao: v })} data-testid={`enviar-votacao-${v.votacao_id}`}><Send className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Enviar</span></Button>
                          )}
                          {(v.status === 'enviada' || v.status === 'respondida') && (
                            <Button variant="outline" size="sm" className="text-red-600 border-red-600 hover:bg-red-50 text-xs h-8" onClick={() => setConfirmAction({ type: 'encerrar', votacao: v })} data-testid={`encerrar-votacao-${v.votacao_id}`}><Lock className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Encerrar</span></Button>
                          )}
                          {(v.status === 'enviada' || v.status === 'respondida' || v.status === 'encerrada') && (
                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-600 hover:bg-blue-50 text-xs h-8" onClick={() => openDetail(v)} data-testid={`ver-respostas-${v.votacao_id}`}><BarChart3 className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline">Respostas</span></Button>
                          )}
                        </>)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Vote className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{search || filter !== 'todos' ? 'Nenhuma votação encontrada' : 'Nenhuma votação criada'}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <VotacaoFormDialog
        open={formOpen}
        onOpenChange={(o) => { if (!o) { setFormOpen(false); setEditingId(null); setForm({ ...emptyForm }); } else setFormOpen(true); }}
        editingId={editingId}
        initialForm={form}
        onSaved={handleSave}
      />

      {/* Detail/Results Dialog */}
      <Dialog open={!!detailVotacao} onOpenChange={() => setDetailVotacao(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resultados</DialogTitle>
          </DialogHeader>
          {detailVotacao && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-slate-900 mb-4">{detailVotacao.pergunta}</p>

              {detailVotacao.tipo === 'multipla_escolha' && detailVotacao.opcoes?.length > 0 && (
                <div className="space-y-4">
                  {detailVotacao.opcoes.map((op, i) => {
                    const count = respostas.filter(r => r.opcoes_selecionadas?.includes(i)).length;
                    const total = respostas.length || 1;
                    const pct = Math.round((count / total) * 100);
                    const isCorreta = detailVotacao.marcar_correta && op.correta;
                    return (
                      <div key={i} className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs text-slate-700 font-medium">{op.texto}</p>
                            {isCorreta && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded">Correta</span>}
                          </div>
                          <span className="text-xs text-slate-500">{count} / {pct}%</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${isCorreta ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {detailVotacao.tipo === 'texto' && (
                <div className="space-y-2">
                  {respostas.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                          <span className="text-[10px] text-slate-400">{fmtDate(r.updated_at || r.timestamp)}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">"{r.texto}"</p>
                      </div>
                    </div>
                  ))}
                  {respostas.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">Nenhuma resposta ainda.</p>
                  )}
                </div>
              )}

              {/* Lista de quem respondeu (múltipla escolha) */}
              {detailVotacao.tipo === 'multipla_escolha' && respostas.length > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Respostas individuais</p>
                  <div className="space-y-2">
                    {respostas.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                            <span className="text-[10px] text-slate-400">{fmtDate(r.updated_at || r.timestamp)}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">
                            "{(r.opcoes_selecionadas || []).map(idx => detailVotacao.opcoes?.[idx]?.texto).filter(Boolean).join(', ')}"
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-500 pt-1 border-t border-slate-100">{respostas.length} {respostas.length === 1 ? 'resposta' : 'respostas'} no total</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === 'enviar' ? 'Enviar Votação' : confirmAction?.type === 'encerrar' ? 'Encerrar Votação' : confirmAction?.type === 'reabrir' ? 'Reabrir Votação' : 'Excluir Votação'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {confirmAction?.type === 'enviar' && 'Ao enviar, a votação ficará visível para todos os auxiliares. Deseja continuar?'}
            {confirmAction?.type === 'encerrar' && 'Ao encerrar, nenhuma nova resposta será aceita. Deseja continuar?'}
            {confirmAction?.type === 'reabrir' && 'Ao reabrir como rascunho, todas as respostas serão apagadas e a votação voltará ao estado inicial. Deseja continuar?'}
            {confirmAction?.type === 'excluir' && `Deseja excluir a votação "${confirmAction?.votacao?.pergunta}"?`}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)}>Cancelar</Button>
            <Button size="sm"
              disabled={saving}
              className={confirmAction?.type === 'enviar' ? 'bg-green-600 hover:bg-green-700' : confirmAction?.type === 'reabrir' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => {
                if (confirmAction.type === 'enviar') handleAction(confirmAction.votacao.votacao_id, 'enviar');
                else if (confirmAction.type === 'encerrar') handleAction(confirmAction.votacao.votacao_id, 'encerrar');
                else if (confirmAction.type === 'reabrir') handleAction(confirmAction.votacao.votacao_id, 'reabrir');
                else handleDelete(confirmAction.votacao.votacao_id);
              }}
              data-testid="confirm-votacao-action">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : confirmAction?.type === 'enviar' ? 'Enviar' : confirmAction?.type === 'encerrar' ? 'Encerrar' : confirmAction?.type === 'reabrir' ? 'Reabrir' : 'Excluir'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
